/**
 * Data-truth for the RFQ journey: what has ACTUALLY been accomplished,
 * derived from the RFQ's embedded BOM/plan data — never from navigation.
 * Both the journey rail and the Quotes & Proposals readiness strip read
 * this, so "done" means the same thing everywhere.
 *
 * The suppliers themselves come from utils/supplierCandidates — the same
 * reading the outreach step's threads are built from, so the rail can never
 * claim "no shortlist" about suppliers step 4 is already writing to.
 */

import {
  candidateHasContactEmail,
  isCandidateShortlisted,
  rfqComponents,
  rfqSupplierCandidates,
} from './supplierCandidates';
import { formatPrice } from './currency';

export interface JourneyReadiness {
  bomDone: boolean;
  criteriaDone: boolean;
  shortlistDone: boolean;
  outreachDone: boolean;
  awardDone: boolean;
  shortlistedCount: number;
  contactedCount: number;
  /** Shortlisted, has a contact email, but no conversation yet. */
  notContactedCount: number;
  /** Shortlisted but unreachable — no contact email exists to send to. */
  noEmailCount: number;
  quotedCount: number;
  negotiatingCount: number;
  /** e.g. ["Zbotic ₹1,050/unit"] — supplier-committed quotes only. */
  quotedSummary: string[];
  /** 1-based step → data-complete. */
  stepDone: Record<number, boolean>;
  /** 1-based step → why it is not complete (undefined when done). */
  stepReason: Record<number, string | undefined>;
}

export const computeJourneyReadiness = (rfq: any): JourneyReadiness => {
  const components: any[] = rfqComponents(rfq);
  const candidates = rfqSupplierCandidates(rfq);

  const shortlisted = candidates.filter(isCandidateShortlisted);
  const contacted = shortlisted.filter((s: any) => (s?.conversations || []).length > 0);
  // A supplier with no contact email can never be messaged (composer and
  // backend both refuse) — gating outreach completion on them would trap the
  // buyer on step 4 forever. They count as unreachable, not as pending work.
  const uncontactable = shortlisted.filter(
    (s: any) => !candidateHasContactEmail(s) && (s?.conversations || []).length === 0
  );
  const pending = shortlisted.filter(
    (s: any) => candidateHasContactEmail(s) && (s?.conversations || []).length === 0
  );
  const quoted = candidates.filter(
    (s: any) => s?.quoteSource === 'SUPPLIER_QUOTED' && typeof s?.quotedPrice === 'number' && s.quotedPrice > 0
  );
  const negotiating = candidates.filter((s: any) => s?.status === 'IN_NEGOTIATION');

  const bomDone = components.length > 0;
  // Mongoose materializes nested defaults, so a brand-new RFQ already carries
  // requirements: { complianceRequirements: [], tradeoffPriorities: [] } —
  // "has any keys" is always true. Only actual content counts.
  const req = rfq?.requirements || {};
  const criteriaDone =
    rfq?.workflow?.SOURCING_CRITERIA === 'COMPLETED' ||
    Boolean(
      req.tradeoffPriorities?.length ||
      req.complianceRequirements?.length ||
      req.desiredLeadTime ||
      req.deliveryLocation ||
      req.sourcingLocation ||
      req.notes
    );
  const shortlistDone = shortlisted.length > 0;
  const outreachDone = shortlisted.length > 0 && pending.length === 0;
  const awardDone = Boolean(rfq?.awardPlan?.components?.length);

  const notContactedCount = pending.length;
  const noEmailCount = uncontactable.length;

  return {
    bomDone,
    criteriaDone,
    shortlistDone,
    outreachDone,
    awardDone,
    shortlistedCount: shortlisted.length,
    contactedCount: contacted.length,
    notContactedCount,
    noEmailCount,
    quotedCount: quoted.length,
    negotiatingCount: negotiating.length,
    quotedSummary: quoted.map(
      (s: any) => `${s.supplierName} ${formatPrice(s.quotedPrice, typeof s.currency === 'string' ? s.currency : 'INR')}/unit`
    ),
    stepDone: { 1: bomDone, 2: criteriaDone, 3: shortlistDone, 4: outreachDone, 5: awardDone },
    stepReason: {
      1: bomDone ? undefined : 'Upload a BOM to start',
      2: criteriaDone ? undefined : 'Set your sourcing criteria',
      3: shortlistDone ? undefined : 'Run discovery and shortlist suppliers',
      4: outreachDone
        ? undefined
        : shortlisted.length === 0
          ? 'Needs a supplier shortlist first'
          : `${notContactedCount} shortlisted supplier${notContactedCount === 1 ? '' : 's'} not contacted yet`
            + (noEmailCount > 0 ? ` (${noEmailCount} more with no contact email)` : ''),
      5: awardDone ? undefined : 'Confirm an award plan',
    },
  };
};

/** First step whose work is not data-complete — where the journey actually is. */
export const firstIncompleteStep = (r: JourneyReadiness): number => {
  for (let n = 1; n <= 5; n++) if (!r.stepDone[n]) return n;
  return 5;
};
